import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateProject } from '../contexts/projects';
import { CREATE_STEPS, EMPTY_VALUES, slugify, toPayload, validate } from '../utils/form';
import FormSteps, { errorsForStep } from '../../../common/components/form-steps/form-steps';
import ProjectFields from './fields';

import '../../clients/styles/form.css';

export default function CreateProjectForm() {
    const navigate = useNavigate();
    const createProject = useCreateProject();

    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);

    // The slug follows the name until somebody types in the slug field. From
    // that moment it is theirs and the name stops touching it — a slug that
    // keeps rewriting itself after being corrected is a fight with the form.
    const [slugTouched, setSlugTouched] = useState(false);

    const isLast = step === CREATE_STEPS.length - 1;

    // Forward validates ONLY this step's fields: an error has to appear next
    // to the field that caused it, not three screens later. Backward validates
    // nothing — nothing has been sent yet.
    const next = () => {
        const found = errorsForStep(validate(values), CREATE_STEPS[step].fields);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        setStep(s => s + 1);
    };

    const change = (field, value) => {
        setValues(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'name' && !slugTouched) next.slug = slugify(value);
            return next;
        });
        if (field === 'slug') setSlugTouched(true);
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Enter on any earlier step advances instead of sending: a form with a
        // single visible button teaches the keyboard the wrong thing.
        if (!isLast) {
            next();
            return;
        }

        const found = validate(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createProject.mutateAsync(toPayload(values));
            toast.success('Proyecto creado');
            navigate(`/admin/projects/${created.id}`, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el proyecto');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nuevo proyecto</h2>
                <p className="client-form__subtitle">
                    Un proyecto es de un cliente y de ahí cuelga todo lo demás:
                    repositorios, tiers y tareas. Arranca en fase de descubrimiento.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <FormSteps steps={CREATE_STEPS} current={step} onStep={setStep} />

                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del proyecto</h3>
                        <p className="client-form__section-text">
                            De quién es, cómo se llama y cómo se lo nombra en las URLs.
                        </p>
                    </div>

                    <ProjectFields
                        visible={CREATE_STEPS[step].fields}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createProject.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/projects" className="client-form__cancel">Cancelar</Link>
                    {step > 0 && (
                        <button
                            type="button"
                            className="client-form__back"
                            onClick={() => setStep(s => s - 1)}
                            disabled={createProject.isPending}
                        >
                            Atrás
                        </button>
                    )}
                    {!isLast
                        ? (
                            <button type="button" className="client-form__submit" onClick={next}>
                                Siguiente
                            </button>
                        )
                        : (
                            <button
                                type="submit"
                                className="client-form__submit"
                                disabled={createProject.isPending}
                            >
                                {createProject.isPending ? 'Guardando...' : 'Crear proyecto'}
                            </button>
                        )}
                </div>
            </form>
        </section>
    );
}
